import { useDisplay } from "@/hooks/use-display";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import { type ReactNode, useEffect } from "react";

interface AlertProps {
  message: ReactNode;
  name: string;
  className?: string;
  autoClose?: number;
  type?: "success" | "error" | "info";
}

export const Alert = ({
  message,
  name,
  className,
  autoClose,
  type = "info",
}: AlertProps) => {
  const display = useDisplay();

  useEffect(() => {
    if (autoClose) {
      if (display.visible[name]) {
        setTimeout(() => {
          display.hide(name);
        }, autoClose);
      }
    }
  }, [display.visible]);

  if (display.visible[name]) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.4,
          scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
        }}
        className={cn(
          "mb-4 flex border-t-4 border-primary-300 bg-primary-50 p-4 text-primary-800",
          className,
          {
            "border-red-300 bg-red-50 p-4 text-red-800": type == "error",
            "border-green-300 bg-green-50 p-4 text-green-800":
              type == "success",
          }
        )}
        role="alert"
      >
        {/* <Info className="w-4 h-4" /> */}
        <div className="ms-3 text-sm font-medium">{message}</div>
        <button
          onClick={() => {
            display.hide(name);
          }}
          type="button"
          className={cn(
            "-mx-1.5 -my-1.5 ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 p-1.5 text-primary-500 hover:bg-primary-200 focus:ring-2 focus:ring-primary-400",
            {
              "bg-red-50  text-red-500 hover:bg-red-200 focus:ring-2 focus:ring-red-400":
                type == "error",
              "bg-green-50  text-green-500 hover:bg-green-200 focus:ring-2 focus:ring-green-400":
                type == "success",
            }
          )}
        >
          <span className="sr-only">Dismiss</span>
          <svg
            className="h-3 w-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </motion.div>
    );
  } else {
    return undefined;
  }
};
